import type { AuthClient } from './client';

export interface AuthGuardLocation {
  href: string;
  pathname: string;
}

export interface AuthBeforeLoadContext<Context> {
  context: Context;
  location: AuthGuardLocation;
}

export interface AuthRedirectOptions {
  to: string;
  search?: { redirect: string };
}

export interface AuthGuardConfig<Context> {
  getAuth(context: Context): AuthClient;
  redirect(options: AuthRedirectOptions): unknown;
}

/** The screens that only make sense mid-challenge, and so must refuse a settled session. */
function isChallengeRoute(pathname: string, routes: AuthClient['routes']): boolean {
  return (
    pathname === routes.changePassword || (routes.mfa !== undefined && pathname === routes.mfa)
  );
}

/**
 * Build the root TanStack Router `beforeLoad` guard without taking a router dependency.
 * The first navigation awaits session restore, so a valid persisted session never flashes `/login`.
 */
export function createAuthGuard<Context>(config: AuthGuardConfig<Context>) {
  return async ({ context, location }: AuthBeforeLoadContext<Context>): Promise<void> => {
    const auth = config.getAuth(context);
    await auth.restore();

    const { routes, state } = auth;
    const isLogin = location.pathname === routes.login;
    const isOAuthCallback = location.pathname === routes.oauthCallback;

    if (state.status === 'authenticated') {
      // HS-1408: a signed-in session has no business on a challenge screen. `changePassword` and
      // `verifyMfa` both refuse to run outside their challenge state and return silently, so the
      // form rendered there looks live and does nothing — no request, no error, no message. The
      // guard used to return here unconditionally, which is what made those screens reachable.
      // `login` is deliberately NOT included: its form works, signing in as another account is a
      // real use, and the OAuth return lands there. Nor is `oauthCallback`, which has to stay
      // reachable while authenticated so the callback screen can settle and navigate itself.
      if (isChallengeRoute(location.pathname, routes)) {
        throw config.redirect({ to: routes.home });
      }
      return;
    }
    if (state.status === 'authenticating' && (isLogin || isOAuthCallback)) return;

    if (state.status === 'mfaRequired') {
      if (routes.mfa && location.pathname === routes.mfa) return;
      throw config.redirect({ to: routes.mfa ?? routes.login });
    }

    if (state.status === 'passwordChangeRequired') {
      if (location.pathname === routes.changePassword) return;
      throw config.redirect({ to: routes.changePassword });
    }

    if (isLogin || isOAuthCallback) return;
    throw config.redirect({ to: routes.login, search: { redirect: location.href } });
  };
}
